import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { apiClient } from '../../api';
import Drawer from '../UI/Drawer';
import { errMessage, inputCls, inputStyle, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle } from '../UI/ink';
import {
  CATEGORIES, CHECKS, FIELD, FIELDS, SEVERITIES, SEV_LABEL, SEV_TOKEN, checksFor, fromApiLeaf, matchSentence, matchWarning, newLeaf, toApiLeaf,
} from './model';

const mono = "'JetBrains Mono', monospace";
const box = { background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' };
const sel = { ...inputStyle, height: 40, background: 'var(--surf-surface)' };
const isGroup = (x) => x && Array.isArray(x.conditions);

const Seg = ({ value, options, onChange, label }) => (
  <span role="group" aria-label={label} className="inline-flex p-[3px] gap-0.5 rounded-[9px] border" style={box}>
    {options.map(([id, t]) => {
      const on = value === id;
      return <button key={id} type="button" aria-pressed={on} onClick={() => onChange(id)} className="h-8 px-3 rounded-md text-[13px]" style={{ background: on ? 'var(--surf-raised)' : 'transparent', color: on ? 'var(--ink-text)' : 'var(--ink-muted)', fontWeight: on ? 600 : 400 }}>{t}</button>;
    })}
  </span>
);

function toEditor(match) {
  const conv = (c) => (isGroup(c) ? { key: Math.random().toString(36).slice(2), logic: c.logic, conditions: c.conditions.filter(x => !isGroup(x)).map(fromApiLeaf) } : fromApiLeaf(c));
  return { logic: match?.logic || 'all', conditions: (match?.conditions || []).map(conv) };
}
function toApi(ed) {
  const conv = (c) => (isGroup(c) ? { logic: c.logic, conditions: c.conditions.map(toApiLeaf) } : toApiLeaf(c));
  return { logic: ed.logic, conditions: ed.conditions.map(conv) };
}

function LeafEditor({ leaf, n, onChange, onRemove }) {
  const f = FIELD[leaf.field];
  const checks = checksFor(leaf.field);
  const c = CHECKS[leaf.check] || CHECKS.equals;
  const set = (patch) => onChange({ ...leaf, ...patch });
  const field = (label, k, props = {}) => (
    <input type="text" aria-label={`${label} for condition ${n}`} value={leaf[k]} onChange={e => set({ [k]: e.target.value })} className="flex-[1_1_120px] min-w-0 h-10 box-border px-2.5 rounded-lg border text-[12.5px] outline-none" style={{ ...sel, fontFamily: mono }} {...props} />
  );
  const windowInputs = (
    <>
      <input type="number" min="1" aria-label={`Window for condition ${n}`} value={leaf.window} onChange={e => set({ window: e.target.value })} className="w-20 h-10 box-border px-2.5 rounded-lg border text-[12.5px]" style={{ ...sel, fontFamily: mono }} />
      <select aria-label={`Window unit for condition ${n}`} value={leaf.unit} onChange={e => set({ unit: e.target.value })} className="h-10 box-border px-2 rounded-lg border text-[13px]" style={sel}><option value="min">min</option><option value="h">hours</option><option value="d">days</option></select>
    </>
  );
  return (
    <div className="flex flex-col gap-2 p-3 rounded-[10px] border" style={box}>
      <span className="flex items-center justify-between">
        <span className="font-mono text-[10.5px] tracking-[0.1em]" style={{ color: 'var(--ink-faint)' }}>CONDITION {n}</span>
        <button type="button" onClick={onRemove} aria-label={`Remove condition ${n}`} className="w-8 h-8 flex items-center justify-center" style={{ color: 'var(--ink-faint)' }}><Trash2 className="w-[15px] h-[15px]" strokeWidth={1.8} /></button>
      </span>
      <div className="flex flex-wrap gap-2">
        <select aria-label={`Field for condition ${n}`} value={leaf.field} onChange={e => { const fld = e.target.value; const ok = checksFor(fld); set({ field: fld, check: ok.includes(leaf.check) ? leaf.check : ok[0] }); }} className="flex-[1_1_200px] min-w-0 h-10 box-border px-2 rounded-lg border text-[13px]" style={sel}>
          {FIELDS.map(x => <option key={x.field} value={x.field}>{x.label} · {x.field}</option>)}
        </select>
        <select aria-label={`Check for condition ${n}`} value={leaf.check} onChange={e => set({ check: e.target.value })} className="flex-[1_1_150px] min-w-0 h-10 box-border px-2 rounded-lg border text-[13px]" style={sel}>
          {checks.map(k => <option key={k} value={k}>{CHECKS[k].label}</option>)}
        </select>
        {c.value === 'one' && field('Value', 'value', { placeholder: leaf.check === 'ip_in_cidr' ? '10.0.0.0/8' : f?.kind === 'num' ? '3389' : '' })}
        {c.value === 'list' && field('Values', 'value', { placeholder: 'comma-separated' })}
        {c.value === 'level' && (
          <select aria-label={`Threat level for condition ${n}`} value={leaf.level} onChange={e => set({ level: e.target.value })} className="flex-[1_1_120px] h-10 box-border px-2 rounded-lg border text-[13px]" style={sel}>
            <option value="high">level ≥ high</option><option value="medium">level ≥ medium</option><option value="low">any level</option>
          </select>
        )}
      </div>
      {c.value === 'window' && <div className="flex flex-wrap items-center gap-2 text-[13px]" style={{ color: 'var(--ink-muted)' }}>not seen in the last {windowInputs}</div>}
      {c.value === 'count' && (
        <div className="flex flex-wrap items-center gap-2 text-[13px]" style={{ color: 'var(--ink-muted)' }}>
          at least <input type="number" min="1" aria-label={`Count for condition ${n}`} value={leaf.count} onChange={e => set({ count: e.target.value })} className="w-20 h-10 box-border px-2.5 rounded-lg border text-[12.5px]" style={{ ...sel, fontFamily: mono }} />
          {leaf.check === 'seen_on_servers' ? 'assets' : 'times'} within {windowInputs}
        </div>
      )}
      <span className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>
        {f?.help}{f && f.source !== 'host' ? ` Arrives in ${f.source === 'snapshot' ? 'snapshots' : 'heartbeats'}.` : ''}
      </span>
    </div>
  );
}

/**
 * Create, edit or duplicate a rule. It reads back what the rule does in
 * plain words, and can replay it over the last 7 days before saving.
 */
export default function RuleDrawer({ open, mode, rule, onClose, onSaved }) {
  const editing = mode === 'edit';
  const copying = mode === 'duplicate';
  const [form, setForm] = useState(null);
  const [ed, setEd] = useState({ logic: 'all', conditions: [] });
  const [mitreInput, setMitreInput] = useState('');
  const [offOriginal, setOffOriginal] = useState(true);
  const [back, setBack] = useState(null);
  const [backBusy, setBackBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const r = rule || {};
    setForm({
      name: copying ? `${r.name} (copy)` : editing ? r.name : '',
      description: r.description || '',
      severity: r.severity || 'high',
      category: r.category || 'network',
      mitre: r.mitre_techniques || [],
      fp: (r.false_positive_hints || []).join('\n'),
    });
    setEd(rule?.match ? toEditor(rule.match) : { logic: 'all', conditions: [newLeaf()] });
    setOffOriginal(true); setBack(null); setError(null); setMitreInput('');
  }, [open, rule, copying, editing]);

  const apiMatch = useMemo(() => toApi(ed), [ed]);
  useEffect(() => { setBack(null); }, [apiMatch]);
  if (!open || !form) return null;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setLeaf = (path, next) => setEd(cur => {
    const copy = { ...cur, conditions: [...cur.conditions] };
    if (path.length === 1) copy.conditions[path[0]] = next;
    else { const g = { ...copy.conditions[path[0]], conditions: [...copy.conditions[path[0]].conditions] }; g.conditions[path[1]] = next; copy.conditions[path[0]] = g; }
    return copy;
  });
  const removeAt = (path) => setEd(cur => {
    if (path.length === 1) return { ...cur, conditions: cur.conditions.filter((_, i) => i !== path[0]) };
    const g = cur.conditions[path[0]];
    const kids = g.conditions.filter((_, i) => i !== path[1]);
    return { ...cur, conditions: kids.length ? cur.conditions.map((c, i) => (i === path[0] ? { ...g, conditions: kids } : c)) : cur.conditions.filter((_, i) => i !== path[0]) };
  });
  const addMitre = () => {
    const t = mitreInput.trim().toUpperCase();
    if (t && !form.mitre.includes(t)) setForm(f => ({ ...f, mitre: [...f.mitre, t] }));
    setMitreInput('');
  };

  const runBack = async () => {
    setBackBusy(true); setError(null);
    try {
      const r = await apiClient.post('/api/rules/backtest', { match: apiMatch, compare_rule_id: copying || editing ? rule?.id : undefined });
      setBack(r.data);
    } catch (err) {
      setError(errMessage(err, 'Could not replay the rule.'));
    } finally {
      setBackBusy(false);
    }
  };

  const save = async (enabled) => {
    setBusy(true); setError(null);
    const body = {
      name: form.name.trim(), description: form.description.trim() || null, severity: form.severity, category: form.category,
      match: apiMatch, mitre_techniques: form.mitre, false_positive_hints: form.fp.split('\n').map(s => s.trim()).filter(Boolean), enabled,
    };
    try {
      if (editing) {
        await apiClient.put(`/api/rules/${rule.id}`, body);
        onSaved(rule.id);
      } else {
        const r = await apiClient.post('/api/rules', { ...body, copied_from: copying ? rule.id : undefined, disable_original: copying && offOriginal });
        onSaved(r.data?.id);
      }
    } catch (err) {
      setError(errMessage(err, 'Could not save the rule.'));
      setBusy(false);
    }
  };

  const warning = matchWarning(apiMatch);
  let n = 0;
  const groupEditor = (g, gi) => (
    <div key={g.key} className="flex flex-col gap-2 p-3 rounded-[12px] border border-dashed" style={{ borderColor: 'var(--surf-line-strong)' }}>
      <span className="flex flex-wrap items-center gap-2.5"><span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>Group: match</span>
        <Seg label="How the group combines" value={g.logic} onChange={(v) => setLeaf([gi], { ...g, logic: v })} options={[['all', 'All'], ['any', 'Any']]} />
        <button type="button" onClick={() => removeAt([gi])} className="ml-auto text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Remove group</button>
      </span>
      {g.conditions.map((l, li) => { n += 1; return <LeafEditor key={l.key} leaf={l} n={n} onChange={(next) => setLeaf([gi, li], next)} onRemove={() => removeAt([gi, li])} />; })}
      <button type="button" onClick={() => setLeaf([gi], { ...g, conditions: [...g.conditions, newLeaf(g.conditions[0]?.field)] })} className="self-start h-9 text-[13px]" style={{ color: 'var(--ink-link)' }}>+ Add condition to group</button>
    </div>
  );
  const nameOk = form.name.trim().length > 0 && ed.conditions.length > 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker={editing ? 'EDIT RULE' : 'NEW RULE'}
      title={editing ? 'Edit detection rule' : 'Create a detection rule'}
      subtitle={copying ? <>Copied from <strong>{rule.name}</strong>. The original is unchanged.</> : editing ? 'Changes apply from the next snapshot each agent sends.' : 'Runs on every snapshot and heartbeat your agents send.'}
      footer={
        <>
          <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Runs on the next snapshot from each agent.</span>
          <div className="flex gap-2">
            {!editing && <button type="button" disabled={busy || !nameOk} onClick={() => save(false)} className={secondaryBtn} style={secondaryBtnStyle}>Save, turned off</button>}
            <button type="button" disabled={busy || !nameOk} onClick={() => save(editing ? rule.enabled : true)} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Save and turn on'}</button>
          </div>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {error && <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>}
        <label className="flex flex-col gap-1.5 text-[13px] font-medium">Name
          <input type="text" value={form.name} onChange={set('name')} autoFocus className={inputCls} style={inputStyle} placeholder="e.g. RDP open outside the lab" />
        </label>
        <label className="flex flex-col gap-1.5 text-[13px] font-medium">Why it matters <span className="font-normal" style={{ color: 'var(--ink-faint)' }}>(shown on every finding)</span>
          <textarea rows={2} value={form.description} onChange={set('description')} className="box-border px-3 py-2.5 rounded-[9px] border text-[13.5px] font-normal leading-normal resize-y outline-none" style={inputStyle} />
        </label>
        <div className="flex flex-wrap gap-4">
          <div className="flex-[2_1_260px] flex flex-col gap-1.5"><span id="b-sev" className="text-[13px] font-medium">Severity</span>
            <div role="group" aria-labelledby="b-sev" className="flex gap-1">
              {SEVERITIES.map(s => {
                const on = form.severity === s;
                return <button key={s} type="button" aria-pressed={on} onClick={() => setForm(f => ({ ...f, severity: s }))} className="flex-1 h-10 flex items-center justify-center gap-[5px] rounded-lg border font-mono text-[10.5px]" style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-bg)', borderColor: on ? 'var(--accent)' : 'var(--surf-line-strong)' }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: `var(--sev-${SEV_TOKEN[s]}-fg)` }} />{SEV_LABEL[s]}</button>;
              })}
            </div>
          </div>
          <label className="flex-[1_1_140px] flex flex-col gap-1.5 text-[13px] font-medium">Category
            <select value={form.category} onChange={set('category')} className="h-10 box-border px-2 rounded-[9px] border text-[13.5px] font-normal" style={inputStyle}>{CATEGORIES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select>
          </label>
        </div>

        <div className="flex flex-col gap-2.5">
          <span className="flex flex-wrap items-center gap-2.5"><span className="text-[13px] font-medium">Fire when an asset’s data matches</span>
            <Seg label="How conditions combine" value={ed.logic} onChange={(v) => setEd(e => ({ ...e, logic: v }))} options={[['all', 'All of these'], ['any', 'Any of these']]} />
          </span>
          {ed.conditions.map((c, i) => (isGroup(c) ? groupEditor(c, i) : (() => { n += 1; return <LeafEditor key={c.key} leaf={c} n={n} onChange={(next) => setLeaf([i], next)} onRemove={() => removeAt([i])} />; })()))}
          <span className="flex gap-4">
            <button type="button" onClick={() => setEd(e => ({ ...e, conditions: [...e.conditions, newLeaf()] }))} className="h-9 inline-flex items-center gap-1 text-[13px]" style={{ color: 'var(--ink-link)' }}><Plus className="w-3.5 h-3.5" />Add condition</button>
            <button type="button" onClick={() => setEd(e => ({ ...e, conditions: [...e.conditions, { key: Math.random().toString(36).slice(2), logic: e.logic === 'all' ? 'any' : 'all', conditions: [newLeaf()] }] }))} className="h-9 inline-flex items-center gap-1 text-[13px]" style={{ color: 'var(--ink-link)' }}><Plus className="w-3.5 h-3.5" />Add group</button>
          </span>
          <div className="flex flex-col gap-1 px-3.5 py-3 rounded-[10px] border" style={{ borderColor: 'var(--surf-line-strong)' }}>
            <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>IN PLAIN WORDS</span>
            <span className="text-sm leading-normal">{matchSentence(apiMatch)}</span>
            {warning && <span className="text-[12.5px] leading-normal" style={{ color: 'var(--sev-high-fg)' }}>{warning}</span>}
          </div>
        </div>

        <div className="flex flex-col gap-2.5 p-3.5 rounded-[10px] border" style={box}>
          <span className="flex flex-wrap items-center justify-between gap-2"><span className="text-[13.5px] font-semibold">Try it on the last 7 days</span>
            <button type="button" disabled={backBusy || !ed.conditions.length} onClick={runBack} className="h-9 px-3 rounded-lg border text-[13px] disabled:opacity-50" style={secondaryBtnStyle}>{backBusy ? 'Replaying…' : back ? 'Run again' : 'Run on last 7 days'}</button>
          </span>
          {!back && <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>See what this rule would have caught before you turn it on.</span>}
          {back && !back.replayable && <span className="text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{back.reason}</span>}
          {back?.replayable && (
            <>
              <span className="text-[13.5px] leading-normal">
                {back.matches === 0 ? <>Would not have fired.</> : <>Would have fired on <strong>{back.matches} snapshot{back.matches === 1 ? '' : 's'}</strong>, raising <strong>{back.findings} finding{back.findings === 1 ? '' : 's'} on {back.asset_count} asset{back.asset_count === 1 ? '' : 's'}</strong>{back.assets.length ? `: ${back.assets.slice(0, 3).map(a => `${a.hostname} (${a.entities.join(', ')})`).join('; ')}` : ''}.</>}
                {back.original && <> The {editing ? 'saved' : 'original'} rule raised {back.original.findings} finding{back.original.findings === 1 ? '' : 's'} on {back.original.assets} asset{back.original.assets === 1 ? '' : 's'}.</>}
              </span>
              <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Checked {back.checked} stored snapshot{back.checked === 1 ? '' : 's'}{back.capped ? ' (the most recent ones)' : ''} from the last {back.days} days. Nothing was saved.</span>
            </>
          )}
        </div>

        <div className="flex flex-col gap-2"><label htmlFor="b-mitre" className="text-[13px] font-medium">MITRE ATT&amp;CK</label>
          <div className="flex flex-wrap items-center gap-1.5 min-h-[42px] box-border px-2 py-[5px] rounded-[9px] border" style={inputStyle}>
            {form.mitre.map(t => (
              <span key={t} className="h-7 inline-flex items-center gap-1 pl-2 pr-1 rounded-md font-mono text-[11.5px]" style={{ background: 'var(--surf-raised)' }}>{t}
                <button type="button" aria-label={`Remove ${t}`} onClick={() => setForm(f => ({ ...f, mitre: f.mitre.filter(x => x !== t) }))} className="w-6 h-6 flex items-center justify-center" style={{ color: 'var(--ink-faint)' }}><X className="w-3 h-3" /></button>
              </span>
            ))}
            <input id="b-mitre" type="text" value={mitreInput} onChange={e => setMitreInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addMitre(); } }} onBlur={addMitre} placeholder="Add a technique, e.g. T1133" className="flex-[1_1_140px] min-w-0 h-[30px] bg-transparent border-0 outline-none text-[13px]" style={{ color: 'var(--ink-text)' }} />
          </div>
        </div>

        <label className="flex flex-col gap-1.5 text-[13px] font-medium">Known false positives <span className="font-normal" style={{ color: 'var(--ink-faint)' }}>(one per line)</span>
          <textarea rows={2} value={form.fp} onChange={set('fp')} className="box-border px-3 py-2.5 rounded-[9px] border text-[13.5px] font-normal leading-normal resize-y outline-none" style={inputStyle} />
        </label>

        {copying && (
          <div className="flex items-center gap-3 px-3.5 py-3 rounded-[10px] border" style={{ borderColor: 'var(--surf-line)' }}>
            <span className="flex-grow flex flex-col gap-0.5"><span className="text-[13.5px] font-medium">Turn off {rule.name} for your organisation</span><span className="text-xs" style={{ color: 'var(--ink-faint)' }}>So both rules don’t raise the same finding.</span></span>
            <Switch on={offOriginal} onClick={() => setOffOriginal(v => !v)} label="Turn off the original rule" />
          </div>
        )}
      </div>
    </Drawer>
  );
}

export function Switch({ on, onClick, label, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={onClick} className="relative w-11 h-[26px] shrink-0 p-0 rounded-[13px] border disabled:opacity-50" style={{ background: on ? 'var(--accent)' : 'var(--surf-raised)', borderColor: on ? 'var(--accent)' : 'var(--surf-line-strong)' }}>
      <span className="absolute top-[3px] w-[18px] h-[18px] rounded-full transition-[left]" style={{ left: on ? 22 : 3, background: on ? '#0A0A0A' : 'var(--ink-muted)' }} />
    </button>
  );
}
